import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { googleClientId, locale } from "src/config/app-config";
import { useAuth } from "src/hooks/useAuth";
import { useDisplayConsentManager } from "src/hooks/stores/use-app-store";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { CONSENT_NOT_REQUIRED_LOCALES } from "src/molecules/ConsentMangerBanner/utils";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import {
  trackGoogleOneTapDismissedEvent,
  trackGoogleOneTapDisplayedEvent,
  trackGoogleOneTapFailedEvent,
} from "src/tracking/events/google-one-tap";
import { SignEvent, SignLocation, segment } from "src/tracking/segment";
import { SIGN_IN_PAGES } from "src/utils/constants";
import { isNativeApp } from "src/utils/isNativeApp";
import { signIn } from "src/utils/nextAuth";
import type {
  GoogleCredentialResponse,
  GooglePromptMomentNotification,
} from "src/types/google-one-tap";
import {
  cancelOneTap,
  initializeOneTap,
  isOneTapSupported,
  promptOneTap,
} from "./google-one-tap";

const SUPPRESSED_PATHNAMES = [
  "/sign-in-or-register",
  "/checkout",
  "/app-login",
];

type GoogleOneTapState = {
  isEligible: boolean;
  handleScriptReady: () => void;
};

function isSuppressedRoute(pathname: string, asPath: string): boolean {
  if (SUPPRESSED_PATHNAMES.some((route) => pathname.startsWith(route))) {
    return true;
  }

  const path = asPath.split("?")[0];

  return SIGN_IN_PAGES.includes(path);
}

export function useGoogleOneTap(): GoogleOneTapState {
  const router = useRouter();
  const { isLoggedIn, isLoading } = useAuth();
  const { googleOneTapEnabled } = useFeatureFlags();
  const displayConsentManager = useDisplayConsentManager();
  const initializedRef = useRef(false);
  const promptedRef = useRef(false);
  const signingInRef = useRef(false);

  const isConsentManagerShown =
    displayConsentManager && !CONSENT_NOT_REQUIRED_LOCALES.includes(locale);

  const isEligible =
    !isLoading &&
    !isLoggedIn &&
    Boolean(googleOneTapEnabled) &&
    !!googleClientId &&
    !isNativeApp &&
    router.isReady &&
    !("app" in router.query) &&
    !isSuppressedRoute(router.pathname, router.asPath) &&
    !isConsentManagerShown &&
    isOneTapSupported();

  const handleCredentialResponse = useCallback(
    (response: GoogleCredentialResponse) => {
      if (signingInRef.current || !response.credential) {
        return;
      }

      signingInRef.current = true;
      segment.signHandler(SignEvent.SIGN_IN_STARTED, {
        location: SignLocation.ONE_TAP,
      });

      void signIn(AuthProvider.GOOGLE_ONE_TAP, {
        credential: response.credential,
        redirect: false,
      })
        .then((result) => {
          if (!result || result.error) {
            signingInRef.current = false;
            void trackGoogleOneTapFailedEvent({
              reason: result?.error || "unknown",
            });

            return;
          }

          cancelOneTap();

          void router.replace(router.asPath, undefined, { scroll: false });
        })
        .catch(() => {
          signingInRef.current = false;
          void trackGoogleOneTapFailedEvent({ reason: "request_failed" });
        });
    },
    [router]
  );

  const handleMoment = useCallback(
    (notification: GooglePromptMomentNotification) => {
      try {
        if (notification.isDisplayed()) {
          void trackGoogleOneTapDisplayedEvent();

          return;
        }

        if (notification.isDismissedMoment()) {
          void trackGoogleOneTapDismissedEvent({
            reason: notification.getDismissedReason(),
          });
        }
      } catch {
        return;
      }
    },
    []
  );

  const handleScriptReady = useCallback(() => {
    if (initializedRef.current || !googleClientId) {
      return;
    }

    initializedRef.current = true;
    initializeOneTap({
      client_id: googleClientId,
      callback: handleCredentialResponse,
      cancel_on_tap_outside: false,
      use_fedcm_for_prompt: true,
      auto_select: false,
      itp_support: true,
      context: "signin",
    });

    if (!promptedRef.current) {
      promptedRef.current = true;
      promptOneTap(handleMoment);
    }
  }, [handleCredentialResponse, handleMoment]);

  useEffect(() => {
    if (isLoggedIn) {
      cancelOneTap();
    }
  }, [isLoggedIn]);

  return { isEligible, handleScriptReady };
}
